import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { Sentry, initSentry } from "./shared/sentry";
import { ToastProvider } from "./shared/components/Toast";
import { ThemeProvider } from "./shared/theme/ThemeProvider";
import { I18nProvider } from "./shared/i18n/I18nProvider";
import { printBootBanner } from "./shared/logger";
import { maybeInitEruda } from "./shared/eruda";
import { cssVariablesStylesheet } from "@isale/design-tokens";
import "./globals.css";

// ADR-011 — inject the design-token CSS variables (:root = light, .dark = dark)
// once, before first paint. Token-mapped classes (bg-primary / text-fg /
// border-default / bg-surface …) resolve to these vars; the ThemeProvider's
// `.dark` root class flips every value with no runtime hook. Single source =
// @isale/design-tokens `cssVariablesStylesheet()`.
const tokenStyleEl = document.createElement("style");
tokenStyleEl.id = "isale-design-tokens";
tokenStyleEl.textContent = cssVariablesStylesheet();
document.head.appendChild(tokenStyleEl);

// Print version banner + DevTools filter hint as the very first console
// line — easy lineup with Sentry events later + Vincent can confirm which
// deploy he's on without opening Network tab.
printBootBanner();

// Eruda — mobile DevTools console for iSale dev builds (loads from CDN,
// no production bundle weight). See `shared/eruda.ts` for the predicates.
maybeInitEruda();

// Sentry first — no-op when VITE_SENTRY_DSN is empty so local dev is
// undisturbed. Once Vincent sets the DSN per `MP7-credential-handover.md`
// STEP F, runtime errors auto-flow to Sentry.
initSentry();

const container = document.getElementById("root");
if (!container) throw new Error("#root missing in index.html");

createRoot(container).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div className="flex h-screen items-center justify-center bg-canvas p-6 text-center">
          <div>
            <div className="text-2xl font-extrabold text-fg">
              Có lỗi xảy ra.
            </div>
            <p className="mt-2 max-w-md text-sm text-fg-muted">
              Sự cố đã được ghi lại. Bấm thử lại hoặc tải lại trang.
            </p>
            <button
              onClick={() => {
                resetError();
                window.location.reload();
              }}
              className="mt-4 rounded-lg bg-primary-fill px-4 py-2 text-sm font-bold text-white"
            >
              Thử lại
            </button>
          </div>
        </div>
      )}
    >
      <ThemeProvider>
        <I18nProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </I18nProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
